import loadJS from "common/utils/request/loadjs";

// CSP `script_src` only allows `recaptcha.net` (not `google.com`).
const RECAPTCHA_API_URL = "https://www.recaptcha.net/recaptcha/api.js?render=explicit";

let recaptchaApiPromise = null;

// Load Google's reCAPTCHA api.js once and resolve when grecaptcha.render is
// available. Subsequent calls return the same promise so we never load the
// script twice. The clones-template-into-DOM flow can't rely on api.js's
// auto-render (one-shot at script init), so callers should explicitly call
// `grecaptcha.render(element, options)` after this resolves.
export function loadRecaptchaApi() {
  if (window.grecaptcha && window.grecaptcha.render) return Promise.resolve();
  if (!recaptchaApiPromise) {
    recaptchaApiPromise = loadJS(RECAPTCHA_API_URL).then(
      () =>
        new Promise(resolve => {
          // grecaptcha.render may not be defined immediately after script load.
          const check = () => {
            if (window.grecaptcha && window.grecaptcha.render) {
              resolve();
            } else {
              setTimeout(check, 50);
            }
          };
          check();
        })
    );
  }
  return recaptchaApiPromise;
}

// Explicitly render the reCAPTCHA widget on the first .g-recaptcha element
// found via getRecaptchaEl() and return the widget id (or null on failure /
// already rendered / no element). The Google api.js auto-render is one-shot
// at init, so dynamically-inserted .g-recaptcha divs (e.g., cloned from a
// <template>) need this explicit render call.
//
// `getRecaptchaEl` is a thunk so callers can re-resolve the live element at
// render time (the original element may have been removed/replaced between
// the call and api.js loading).
export function renderRecaptchaWidget(getRecaptchaEl, { callback, expiredCallback } = {}) {
  const $initial = getRecaptchaEl();
  if (!$initial) return Promise.resolve(null);
  const { sitekey } = $initial.dataset;
  if (!sitekey) return Promise.resolve(null);

  return loadRecaptchaApi()
    .then(() => {
      const $live = getRecaptchaEl();
      if (!$live) return null;
      if ($live.dataset.recaptchaRendered === "true") return null;
      try {
        const widgetId = window.grecaptcha.render($live, {
          sitekey,
          callback,
          "expired-callback": expiredCallback
        });
        $live.dataset.recaptchaRendered = "true";
        return widgetId;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Failed to render reCAPTCHA widget:", e);
        return null;
      }
    })
    .catch(err => {
      // eslint-disable-next-line no-console
      console.error("Failed to load reCAPTCHA api.js:", err);
      return null;
    });
}
